// Page Casques - V10 — 5 casques + badge doré MASE
// Gauche: émotionnel (casques, titre, message) | Droite: rationnel (score, barre, calcul)

import { useEffect, useState, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Card, CardContent } from '../components/ui'
import { trackEvent } from '../lib/analytics'
import { DiagnosticLayout, useDiagnosticSteps, useDiagnosticData, useDiagnosticProgress } from '../components/layout'
import { Alert, AlertDescription } from '../components/ui/alert'
import { ScoreProgressBar } from '../components/results'
import { CasqueExplanation } from '../components/results/CasqueDisplay'
import { SEUILS_CASQUES, getCasques, getCasquesAffiches, type Casque } from '@autodiag/shared'
import { Info } from 'lucide-react'
import { cn } from '../lib/utils'
import { api } from '../services/api'


interface ReponseData {
  questionId: string
  metierId?: string
  valeur: number
}

// Messages motivationnels par niveau de casque (1-5)
const LEVEL_MESSAGES: Record<Casque, string> = {
  1: "C'est le début du parcours. Chaque amélioration compte !",
  2: 'Premiers pas ! Continuez vos efforts pour progresser.',
  3: 'Bonne progression. Quelques améliorations pour atteindre l\'excellence.',
  4: 'Bon niveau de prévention ! Maintenez vos bonnes pratiques.',
  5: 'Niveau exemplaire. Vous êtes un modèle pour le secteur !',
}

export default function CasquesPage() {
  const { id: diagnosticId } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { metierIds, metiersSelectionnes, diagnosticState, isLoadingData } = useDiagnosticData(diagnosticId)

  const [reponses, setReponses] = useState<ReponseData[]>([])
  const [attestation, setAttestation] = useState<{ statut: string; badgeDore: boolean } | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Animation
  const [animatedProgress, setAnimatedProgress] = useState(0)
  const [animatedCasques, setAnimatedCasques] = useState(0)

  const diagnosticProgress = useDiagnosticProgress(diagnosticId)

  const steps = useDiagnosticSteps('resultats', diagnosticId, {
    totalMetiers: metierIds.length,
    currentMetierIndex: metierIds.length - 1,
    q2Completed: true,
    metiersSelectionnes: metiersSelectionnes.map(m => ({ code: m.code, nom: m.nom })),
  }, diagnosticProgress, 'resultats-casques')

  useEffect(() => {
    async function loadData() {
      if (!diagnosticId) return

      setIsLoading(true)
      setError(null)

      try {
        const diagnostic = await api.getDiagnostic(diagnosticId)
        const reponsesData = diagnostic.reponses || []
        setReponses(reponsesData)
        if (diagnostic.attestation) {
          setAttestation(diagnostic.attestation)
        }

        if (reponsesData.length === 0) {
          setError('Aucune réponse trouvée pour ce diagnostic')
        }
      } catch (err) {
        console.error('Erreur chargement:', err)
        setError('Impossible de charger les données')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [diagnosticId])

  // Calcul du score total
  const scoreTotal = useMemo(() => {
    return reponses.reduce((sum, r) => sum + r.valeur, 0)
  }, [reponses])

  // Seuils pour le nombre de métiers
  const nbMetiers = Math.min(3, Math.max(1, metierIds.length)) as 1 | 2 | 3
  const seuilsActuels = SEUILS_CASQUES[nbMetiers]

  // Calcul du nombre de casques — direct, plus de cap
  const casques = useMemo((): Casque => {
    if (reponses.length === 0) return 1
    return getCasques(scoreTotal, nbMetiers)
  }, [scoreTotal, nbMetiers, reponses.length])

  // Casques affiches (cap a 4 si score=5 sans attestation validee)
  const displayInfo = useMemo(() => {
    return getCasquesAffiches(casques, attestation)
  }, [casques, attestation])

  // Prochain palier
  const prochainPalier = useMemo(() => {
    if (casques >= 5) return null
    const nextSeuil = seuilsActuels[casques] // casques est 1-indexed, seuils[casques] = seuil du niveau suivant
    return {
      niveau: casques + 1,
      seuil: nextSeuil.min,
      pointsRestants: nextSeuil.min - scoreTotal,
    }
  }, [scoreTotal, casques, seuilsActuels])

  // Animation
  useEffect(() => {
    if (isLoading || isLoadingData || reponses.length === 0) return

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) {
      setAnimatedProgress(scoreTotal)
      setAnimatedCasques(displayInfo.casques)
      return
    }

    const targetCasques = displayInfo.casques
    const duration = 1500
    const startTime = Date.now()

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)

      setAnimatedProgress(Math.round(eased * scoreTotal))
      setAnimatedCasques(Math.min(targetCasques, Math.round(eased * targetCasques)))

      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        setAnimatedProgress(scoreTotal)
        setAnimatedCasques(targetCasques)
      }
    }

    requestAnimationFrame(animate)
  }, [displayInfo.casques, scoreTotal, reponses.length, isLoading, isLoadingData])



  const handleGoToThemes = () => {
    trackEvent('choisir les themes prioritaires')
    navigate(`/diagnostic/${diagnosticId}/themes`, {
      state: diagnosticState,
    })
  }

  const handleGoBack = () => {
    navigate(`/diagnostic/${diagnosticId}/synthese-2`, {
      state: diagnosticState,
    })
  }

  if (isLoading || isLoadingData) {
    return (
      <DiagnosticLayout
        steps={steps}
        showNavigation={false}
        diagnosticState={diagnosticState}
      >
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-muted-foreground">Calcul de votre niveau...</p>
          </div>
        </div>
      </DiagnosticLayout>
    )
  }

  if (error) {
    return (
      <DiagnosticLayout
        steps={steps}
        showNavigation={false}
        diagnosticState={diagnosticState}
      >
        <div className="flex items-center justify-center py-16">
          <Card className="max-w-md w-full">
            <CardContent>
              <div className="text-center">
                <svg
                  className="w-12 h-12 text-destructive mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <h2 className="text-xl font-semibold text-foreground mb-2">Erreur</h2>
                <p className="text-muted-foreground mb-4">{error}</p>
                <Button variant="outline" onClick={() => navigate('/')}>
                  Retour à l'accueil
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DiagnosticLayout>
    )
  }

  return (
    <DiagnosticLayout
      steps={steps}
      onBack={handleGoBack}
      onNext={handleGoToThemes}
      backLabel="Analyse détaillée"
      nextLabel="Choisir les thèmes prioritaires"
      diagnosticState={diagnosticState}
    >
      {/* Header de page */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Votre niveau de prévention</h1>
      </div>

      {/* Layout pleine largeur */}
      <div className="space-y-4">

        {/* Card héros — 2 colonnes */}
        <Card className="overflow-hidden bg-gradient-to-br from-primary/5 via-transparent to-transparent">
            <CardContent className="p-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[400px]">

                {/* ── Gauche : Casques + Titre (émotionnel) ── */}
                <div className="flex flex-col items-center justify-center p-8 sm:p-10 text-center space-y-6 lg:border-r lg:border-border">

                  {/* 5 Casques + médaille dorée si 5 */}
                  <div className="flex items-end justify-center gap-3 sm:gap-4">
                    {[1, 2, 3, 4, 5].map((i) => {
                      const isActive = i <= animatedCasques

                      return (
                        <img
                          key={i}
                          src={isActive ? '/images/casque-actif.svg' : '/images/casque-desactive.svg'}
                          alt={isActive ? 'Casque actif' : 'Casque inactif'}
                          className={cn(
                            'w-12 h-12 sm:w-16 sm:h-16 transition-all',
                            isActive ? 'scale-100' : 'scale-90'
                          )}
                        />
                      )
                    })}
                    {displayInfo.badgeDore && (
                      <img
                        src="/images/medaille-dore.svg"
                        alt="Badge doré"
                        className="w-10 h-10 sm:w-14 sm:h-14 ml-2 transition-all"
                      />
                    )}
                  </div>

                  {/* Titre casques */}
                  <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                    {animatedCasques} casque{animatedCasques !== 1 ? 's' : ''} sur 5
                  </h2>

                  {/* Message motivationnel */}
                  <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">
                    {LEVEL_MESSAGES[displayInfo.casques as Casque] || LEVEL_MESSAGES[4]}
                  </p>

                  {/* Note attestation / badge doré */}
                  <div className="max-w-xs text-left">
                    <Alert className="border-border">
                      <Info className="size-4" />
                      <AlertDescription>
                        {displayInfo.eligible5eme ? (
                          <>
                            Votre score vous rend éligible au 5e casque.
                            Soumettez votre CPP, certification MASE ou ISO 45001 sur la page d'attestation pour l'obtenir.
                          </>
                        ) : displayInfo.badgeDore ? (
                          <>
                            Félicitations ! Votre certification a été validée. Vous avez obtenu le badge doré.
                          </>
                        ) : (
                          <>Générez votre attestation après avoir défini votre plan d'actions.</>
                        )}
                      </AlertDescription>
                    </Alert>
                  </div>
                </div>

                {/* ── Droite : Score + Barre + Détail (rationnel) ── */}
                <div className="flex flex-col items-center justify-center p-8 sm:p-10 space-y-6">

                  {/* Score */}
                  <div className="text-center">
                    <span className="text-5xl sm:text-6xl font-extrabold text-primary tabular-nums">{animatedProgress}</span>
                    <span className="text-lg text-muted-foreground ml-2">points</span>
                  </div>

                  {/* Barre segmentée — pleine largeur */}
                  <div className="w-full max-w-sm">
                    <ScoreProgressBar
                      score={animatedProgress}
                      seuils={seuilsActuels}
                      compact={false}
                    />
                  </div>

                  {/* Prochain palier */}
                  {prochainPalier && (
                    <p className="text-sm text-muted-foreground">
                      Plus que <span className="font-semibold text-primary">{prochainPalier.pointsRestants} pts</span> pour {prochainPalier.niveau} casque{prochainPalier.niveau > 1 ? 's' : ''}
                    </p>
                  )}

                  {/* Tableau des seuils */}
                  <div className="w-full max-w-sm">
                    <CasqueExplanation nombreMetiers={metierIds.length} />
                  </div>

                </div>

              </div>
            </CardContent>
        </Card>

      </div>
    </DiagnosticLayout>
  )
}
